import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Download, Filter, Plus, RefreshCw } from "lucide-react";
import api from "@/lib/api";
import AppShell from "@/components/AppShell";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/lib/auth";
import RequestsTable from "@/components/RequestsTable";
import RequestDetailDialog from "@/components/RequestDetailDialog";
import HolidayCalendarView from "@/components/HolidayCalendarView";
import EmployeesAdmin from "@/components/EmployeesAdmin";
import SupervisorsAdmin from "@/components/SupervisorsAdmin";
import EmailLogs from "@/components/EmailLogs";
import CodesAdmin from "@/components/CodesAdmin";
import ClearHistoryButton from "@/components/ClearHistoryButton";
import DayHolidaysDialog from "@/components/DayHolidaysDialog";
import AdminAddRequestDialog from "@/components/AdminAddRequestDialog";
import TodayOverview from "@/components/TodayOverview";
import TeamCalendar from "@/components/TeamCalendar";
import DepartmentHeatmap from "@/components/DepartmentHeatmap";
import DepartmentsAdmin from "@/components/DepartmentsAdmin";
import PlanningModule from "@/components/PlanningModule";
import SitesAdmin from "@/components/SitesAdmin";
import JobRolesAdmin from "@/components/JobRolesAdmin";
import RemindersAdmin from "@/components/RemindersAdmin";
import { toISODate } from "@/lib/utils2";

export default function AdminDashboard() {
  const { user } = useAuth();
  const [reference, setReference] = useState(null);
  const [requests, setRequests] = useState([]);
  const [stats, setStats] = useState({ pending: 0, approved: 0, rejected: 0, cancelled: 0, total: 0 });
  const [open, setOpen] = useState(null);
  const [filters, setFilters] = useState({
    status: "", department: "", supervisor_id: "", employee_q: "", job_role: "", start_from: "", start_to: "",
  });
  const [tab, setTab] = useState("team");
  const [dayContext, setDayContext] = useState(null); // {date, requests}
  const [addOpen, setAddOpen] = useState(false);
  const [addPrefill, setAddPrefill] = useState(null);

  const loadRef = () => api.get("/reference/all").then(({ data }) => setReference(data));

  const load = async () => {
    const params = {};
    Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
    const [{ data: rs }, { data: st }] = await Promise.all([
      api.get("/holiday-requests", { params }),
      api.get("/stats"),
    ]);
    setRequests(rs); setStats(st);
  };

  useEffect(() => { loadRef(); }, []);
  useEffect(() => { load(); /* eslint-disable-line */ }, [JSON.stringify(filters)]);

  // Deep-link support: ?request=<id> from notification bell.
  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    const sp = new URLSearchParams(location.search);
    const rid = sp.get("request");
    if (!rid) return;
    api.get(`/holiday-requests/${rid}`).then(({ data }) => setOpen(data)).catch(() => {});
    sp.delete("request");
    navigate({ pathname: location.pathname, search: sp.toString() ? `?${sp.toString()}` : "" }, { replace: true });
  }, [location.search]); // eslint-disable-line


  const exportCSV = () => {
    const headers = ["Status","First name","Last name","Email","Department","Site","Job role","Supervisor","Start","End","Days","Submitted","Decision by","Decision at","Reason","Notes"];
    const rows = requests.map((r) => [
      r.status, r.first_name, r.last_name, r.email, r.department, r.site||"", r.job_role,
      r.supervisor_name, r.start_date, r.end_date, r.days, r.submitted_at,
      r.decision_by_name||"", r.decision_at||"", r.decision_reason||"", (r.notes||"").replace(/\n/g," "),
    ]);
    const csv = [headers, ...rows].map((row) =>
      row.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")
    ).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `pnc-absence-notifications-${new Date().toISOString().slice(0,10)}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  const clearFilters = () => setFilters({ status:"", department:"", supervisor_id:"", employee_q:"", job_role:"", start_from:"", start_to:"" });

  return (
    <AppShell>
      <section className="max-w-[1400px] mx-auto px-3 sm:px-6 py-5 sm:py-8 space-y-5 sm:space-y-6">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <h1 className="font-heading text-3xl font-semibold">Admin dashboard</h1>
            <p className="text-sm text-muted-foreground">Welcome back, {user.name}. Manage all absences across every site.</p>
          </div>
          <div className="flex gap-2">
            <Button data-testid="admin-add-request-btn" size="sm" onClick={() => setAddOpen(true)} className="gap-1.5">
              <Plus className="w-3.5 h-3.5"/> Add absence notification
            </Button>
            <ClearHistoryButton onCleared={() => { load(); loadRef(); }}/>
            <Button data-testid="admin-export-btn" variant="outline" size="sm" onClick={exportCSV} className="gap-1.5"><Download className="w-3.5 h-3.5"/> Export CSV</Button>
            <Button data-testid="admin-refresh-btn" variant="outline" size="sm" onClick={() => { load(); loadRef(); }} className="gap-1.5"><RefreshCw className="w-3.5 h-3.5"/> Refresh</Button>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          <Stat label="Total" value={stats.total}/>
          <Stat label="Pending" value={stats.pending} kind="pending"/>
          <Stat label="Approved" value={stats.approved} kind="approved"/>
          <Stat label="Rejected" value={stats.rejected} kind="rejected"/>
          <Stat label="Cancelled" value={stats.cancelled} kind="cancelled"/>
        </div>

        <TodayOverview
          refreshKey={JSON.stringify({ r: requests.length, s: stats.total })}
          onOpenRequest={setOpen}
          onPickDepartment={(d) => setFilters((f) => ({ ...f, department: d === f.department ? "" : d }))}
        />

        <Card>
          <CardContent className="p-3 sm:p-5 space-y-4">
            {/* Filters */}
            <div className="flex flex-wrap gap-2 items-center">
              <Filter className="w-4 h-4 text-muted-foreground"/>
              <Input data-testid="filter-employee-q" placeholder="Contractor / email…" className="max-w-[220px]" value={filters.employee_q} onChange={(e) => setFilters({ ...filters, employee_q: e.target.value })}/>
              <FilterSelect testid="filter-status" placeholder="All statuses" value={filters.status} onChange={(v) => setFilters({ ...filters, status: v })} options={[{value:"pending",label:"Pending"},{value:"approved",label:"Approved"},{value:"rejected",label:"Rejected"},{value:"cancelled",label:"Cancelled"}]}/>
              <FilterSelect testid="filter-department" placeholder="All departments" value={filters.department} onChange={(v) => setFilters({ ...filters, department: v })} options={(reference?.departments || []).map((d) => ({ value: d.name, label: d.name }))}/>
              <FilterSelect testid="filter-supervisor" placeholder="All supervisors" value={filters.supervisor_id} onChange={(v) => setFilters({ ...filters, supervisor_id: v })} options={(reference?.supervisors || []).map((s) => ({ value: s.id, label: s.name }))}/>
              <FilterSelect testid="filter-job-role" placeholder="All job roles" value={filters.job_role} onChange={(v) => setFilters({ ...filters, job_role: v })} options={(reference?.job_roles || []).map((r) => ({ value: r, label: r }))}/>
              <Input data-testid="filter-start-from" type="date" className="max-w-[170px]" value={filters.start_from} onChange={(e) => setFilters({ ...filters, start_from: e.target.value })}/>
              <span className="text-muted-foreground text-sm">→</span>
              <Input data-testid="filter-start-to" type="date" className="max-w-[170px]" value={filters.start_to} onChange={(e) => setFilters({ ...filters, start_to: e.target.value })}/>
              <Button data-testid="clear-filters-btn" variant="ghost" size="sm" onClick={clearFilters}>Clear</Button>
              <div className="ml-auto text-sm text-muted-foreground">{requests.length} results</div>
            </div>

            <Tabs value={tab} onValueChange={setTab}>
              <TabsList data-testid="admin-tabs" className="flex flex-nowrap overflow-x-auto max-w-full justify-start">
                <TabsTrigger value="team" data-testid="admin-tab-team">Team calendar</TabsTrigger>
                <TabsTrigger value="calendar" data-testid="admin-tab-calendar">Month</TabsTrigger>
                <TabsTrigger value="heatmap" data-testid="admin-tab-heatmap">Heatmap</TabsTrigger>
                <TabsTrigger value="table" data-testid="admin-tab-table">Table</TabsTrigger>
                <TabsTrigger value="employees" data-testid="admin-tab-employees">Contractors</TabsTrigger>
                <TabsTrigger value="supervisors" data-testid="admin-tab-supervisors">Supervisors</TabsTrigger>
                <TabsTrigger value="departments" data-testid="admin-tab-departments">Departments</TabsTrigger>
                <TabsTrigger value="sites" data-testid="admin-tab-sites">Sites</TabsTrigger>
                <TabsTrigger value="job-roles" data-testid="admin-tab-job-roles">Job roles</TabsTrigger>
                <TabsTrigger value="planning" data-testid="admin-tab-planning">Planning</TabsTrigger>
                <TabsTrigger value="reminders" data-testid="admin-tab-reminders">Reminders</TabsTrigger>
                <TabsTrigger value="codes" data-testid="admin-tab-codes">Codes</TabsTrigger>
                <TabsTrigger value="emails" data-testid="admin-tab-emails">Email log</TabsTrigger>
              </TabsList>

              <TabsContent value="team" className="mt-4">
                <TeamCalendar
                  reference={reference}
                  onOpenRequest={setOpen}
                  onChanged={load}
                  onAddRequest={(date, emp) => {
                    setAddPrefill({ date, employee: emp });
                    setAddOpen(true);
                  }}
                />
              </TabsContent>
              <TabsContent value="calendar" className="mt-4">
                <HolidayCalendarView
                  requests={requests}
                  onOpen={setOpen}
                  jobRoles={reference?.job_roles || []}
                  activeRole={filters.job_role}
                  onRoleClick={(r) => setFilters({ ...filters, job_role: r === filters.job_role ? "" : r })}
                  onDayClick={(date, list) => setDayContext({ date, requests: list })}
                />
              </TabsContent>
              <TabsContent value="heatmap" className="mt-4">
                <DepartmentHeatmap reference={reference}/>
              </TabsContent>
              <TabsContent value="table" className="mt-4">
                <RequestsTable rows={requests} onOpen={setOpen} emptyMessage="No requests match these filters."/>
              </TabsContent>
              <TabsContent value="employees" className="mt-4">
                <EmployeesAdmin reference={reference}/>
              </TabsContent>
              <TabsContent value="supervisors" className="mt-4">
                <SupervisorsAdmin reference={reference} onReload={loadRef}/>
              </TabsContent>
              <TabsContent value="departments" className="mt-4">
                <DepartmentsAdmin onChanged={loadRef}/>
              </TabsContent>
              <TabsContent value="sites" className="mt-4">
                <SitesAdmin onChanged={loadRef}/>
              </TabsContent>
              <TabsContent value="job-roles" className="mt-4">
                <JobRolesAdmin onChanged={loadRef}/>
              </TabsContent>
              <TabsContent value="planning" className="mt-4">
                <PlanningModule/>
              </TabsContent>
              <TabsContent value="reminders" className="mt-4">
                <RemindersAdmin reference={reference}/>
              </TabsContent>
              <TabsContent value="codes" className="mt-4">
                <CodesAdmin/>
              </TabsContent>
              <TabsContent value="emails" className="mt-4">
                <EmailLogs/>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </section>

      <RequestDetailDialog
        request={open}
        onClose={() => setOpen(null)}
        onUpdated={(updated) => {
          setRequests((rs) => updated.__deleted ? rs.filter((r) => r.id !== updated.id) : rs.map((r) => r.id === updated.id ? updated : r));
          load();
        }}
        supervisors={reference?.supervisors || []}
        departments={reference?.departments || []}
      />
      <DayHolidaysDialog
        date={dayContext?.date}
        requests={dayContext?.requests || []}
        onClose={() => setDayContext(null)}
        onOpenRequest={(r) => { setDayContext(null); setOpen(r); }}
      />
      <AdminAddRequestDialog
        open={addOpen}
        onOpenChange={(v) => { setAddOpen(v); if (!v) setAddPrefill(null); }}
        reference={reference}
        prefill={addPrefill}
        onCreated={() => { load(); }}
      />
    </AppShell>
  );
}

function Stat({ label, value, kind }) {
  return (
    <div className={`rounded-lg p-3 sm:p-4 border min-w-0 ${kind ? `status-${kind}` : "bg-[#1E1E1E] border-border"}`}>
      <div className="text-[10px] sm:text-[11px] uppercase tracking-[0.08em] font-semibold opacity-80 truncate">{label}</div>
      <div className="font-heading text-2xl sm:text-3xl font-semibold mt-1 tabular-nums">{value}</div>
    </div>
  );
}

function FilterSelect({ testid, placeholder, value, onChange, options }) {
  return (
    <Select value={value || "__all"} onValueChange={(v) => onChange(v === "__all" ? "" : v)}>
      <SelectTrigger data-testid={testid} className="max-w-[200px]"><SelectValue placeholder={placeholder}/></SelectTrigger>
      <SelectContent>
        <SelectItem value="__all">{placeholder}</SelectItem>
        {options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}
